import { motion } from "framer-motion";
import { PILLARS } from "../data/site";

const EASE = [0.16, 1, 0.3, 1];

// Four-pillar architectural framework — 2×2 grid with center diagram.
// Labels live in dedicated grid cells so they NEVER overlap the diagram.

export const PillarFramework = () => {
    return (
        <div data-testid="pillar-framework" className="relative w-full max-w-[760px] mx-auto">
            <div className="grid grid-cols-2 gap-6 md:gap-10 relative">
                {/* Top-left — Presence */}
                <PillarCell
                    data={PILLARS[0]}
                    align="left"
                    q="Q1"
                    delay={0.2}
                    className="md:pr-12"
                />
                {/* Top-right — Trust */}
                <PillarCell
                    data={PILLARS[1]}
                    align="right"
                    q="Q2"
                    delay={0.32}
                    className="md:pl-12"
                />
                {/* Bottom-left — Performance */}
                <PillarCell
                    data={PILLARS[2]}
                    align="left"
                    q="Q3"
                    delay={0.44}
                    className="md:pr-12"
                />
                {/* Bottom-right — Growth */}
                <PillarCell
                    data={PILLARS[3]}
                    align="right"
                    q="Q4"
                    delay={0.56}
                    className="md:pl-12"
                />

                {/* Center diagram (overlay, pointer-events none) */}
                <div className="hidden md:flex absolute inset-0 items-center justify-center pointer-events-none">
                    <svg
                        viewBox="0 0 200 200"
                        className="w-44 h-44 lg:w-52 lg:h-52"
                        aria-hidden
                    >
                        <motion.circle
                            cx="100" cy="100" r="92"
                            fill="none" stroke="#111" strokeWidth="0.8"
                            strokeDasharray="2 4"
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.4, delay: 0.4, ease: EASE }}
                        />
                        <motion.circle
                            cx="100" cy="100" r="64"
                            fill="white" stroke="#111" strokeWidth="1"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
                        />
                        {/* Cross hairs (short) */}
                        <motion.line
                            x1="22" y1="100" x2="36" y2="100"
                            stroke="#111" strokeWidth="0.8"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.9 }}
                        />
                        <motion.line
                            x1="164" y1="100" x2="178" y2="100"
                            stroke="#111" strokeWidth="0.8"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.9 }}
                        />
                        <motion.line
                            x1="100" y1="22" x2="100" y2="36"
                            stroke="#111" strokeWidth="0.8"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.9 }}
                        />
                        <motion.line
                            x1="100" y1="164" x2="100" y2="178"
                            stroke="#111" strokeWidth="0.8"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.9 }}
                        />
                        <motion.circle
                            cx="100" cy="100" r="4" fill="#111"
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 1.0, ease: EASE }}
                        />
                        <text
                            x="100" y="115" textAnchor="middle"
                            style={{
                                fontFamily: "Satoshi, sans-serif",
                                fontSize: 7,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                fill: "#111",
                            }}
                        >
                            Business core
                        </text>
                        {/* Pin dots at the four corner connections */}
                        <circle cx="50" cy="50" r="2.4" fill="#111" />
                        <circle cx="150" cy="50" r="2.4" fill="#111" />
                        <circle cx="50" cy="150" r="2.4" fill="#111" />
                        <circle cx="150" cy="150" r="2.4" fill="#111" />
                    </svg>
                </div>
            </div>

            {/* Mobile-only: tiny diagram below */}
            <div className="md:hidden mt-10 flex justify-center">
                <svg viewBox="0 0 200 200" className="w-32 h-32" aria-hidden>
                    <circle cx="100" cy="100" r="92" fill="none" stroke="#111" strokeWidth="0.8" strokeDasharray="2 4" />
                    <circle cx="100" cy="100" r="64" fill="white" stroke="#111" strokeWidth="1" />
                    <circle cx="100" cy="100" r="4" fill="#111" />
                    <text x="100" y="115" textAnchor="middle" style={{ fontFamily: "Satoshi, sans-serif", fontSize: 7, letterSpacing: "0.28em", textTransform: "uppercase", fill: "#111" }}>
                        Business core
                    </text>
                </svg>
            </div>
        </div>
    );
};

const PillarCell = ({ data, align, q, delay, className = "" }) => {
    const isRight = align === "right";
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay, ease: EASE }}
            className={`flex flex-col gap-2 ${isRight ? "items-end text-right" : "items-start text-left"} ${className}`}
            data-testid={`pillar-${data.title.toLowerCase()}`}
        >
            <span className="text-[10px] tracking-[0.28em] uppercase text-[#444444]">
                {q} · {data.n}
            </span>
            <h3 className="font-serif text-3xl md:text-4xl text-[#111111] leading-none">
                {data.title}
            </h3>
            <p className="mt-2 text-[#444444] text-sm leading-relaxed max-w-[16rem]">
                {data.body}
            </p>
        </motion.div>
    );
};
